const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const normalizeContextValue = (value) => value === null || value === undefined || value === ''
    ? null
    : value;

const buildPayload = (context) => ({
    ga_client_id: context.clientId,
    ga_session_id: context.sessionId,
    ga_observation_id: context.observationId,
    ga_context_source: context.source,
});

const contextSignature = (context) => JSON.stringify([
    context.clientId,
    context.sessionId,
    context.observationId,
    context.source,
]);

export default function loanVerifyPageContext(initialContext = {}) {
    return {
        gaContext: null,
        gaContextSignature: '',

        init() {
            window.addEventListener('ga-context-ready', (event) => {
                this.applyGaContext(event?.detail || {});
            });

            window.addEventListener('ga-session-id-ready', (event) => {
                const detail = event?.detail || {};
                const sessionContext = {sessionId: detail.sessionId};
                if (hasOwn(detail, 'observationId')) {
                    sessionContext.observationId = detail.observationId;
                }
                if (hasOwn(detail, 'source')) {
                    sessionContext.source = detail.source;
                }

                this.applyGaContext(sessionContext);
            });

            this.applyGaContext(window.__vsebankiGaContext || initialContext);
            window.dispatchEvent(new CustomEvent('ga-transport-requested'));
        },

        applyGaContext(detail = {}) {
            const current = this.gaContext || {
                clientId: null,
                sessionId: null,
                observationId: null,
                source: null,
            };

            const hasIncomingValue = ['clientId', 'sessionId', 'observationId', 'source']
                .some((key) => hasOwn(detail, key));

            if (!hasIncomingValue) {
                return;
            }

            const next = {...current};
            const incomingObservationId = hasOwn(detail, 'observationId')
                ? normalizeContextValue(detail.observationId)
                : current.observationId;
            const hasExistingIdentifiers = current.clientId !== null || current.sessionId !== null;
            const observationChanged = incomingObservationId !== null
                && incomingObservationId !== current.observationId
                && (current.observationId !== null || hasExistingIdentifiers);
            const sameObservation = hasOwn(detail, 'observationId')
                && incomingObservationId !== null
                && incomingObservationId === current.observationId;
            const incomingClientId = hasOwn(detail, 'clientId')
                ? normalizeContextValue(detail.clientId)
                : null;
            const incomingSessionId = hasOwn(detail, 'sessionId')
                ? normalizeContextValue(detail.sessionId)
                : null;
            const clientChanged = !observationChanged
                && !sameObservation
                && incomingClientId !== null
                && (current.clientId === null
                    ? current.sessionId !== null
                    : incomingClientId !== current.clientId);
            const sessionChanged = !observationChanged
                && !sameObservation
                && incomingSessionId !== null
                && (current.sessionId === null
                    ? current.clientId !== null
                    : incomingSessionId !== current.sessionId);

            if (observationChanged) {
                next.clientId = null;
                next.sessionId = null;
            } else if (clientChanged) {
                next.sessionId = null;
            } else if (sessionChanged) {
                next.clientId = null;
            }

            if ((clientChanged || sessionChanged) && !hasOwn(detail, 'observationId')) {
                next.observationId = null;
            }

            ['clientId', 'sessionId', 'observationId', 'source'].forEach((key) => {
                if (hasOwn(detail, key)
                    && !(sameObservation
                        && (key === 'clientId' || key === 'sessionId')
                        && normalizeContextValue(detail[key]) === null)
                ) {
                    next[key] = normalizeContextValue(detail[key]);
                }
            });

            const signature = contextSignature(next);
            if (signature === this.gaContextSignature) {
                return;
            }

            this.gaContext = next;
            this.gaContextSignature = signature;

            if (!window.Livewire) {
                return;
            }

            const payload = buildPayload(next);
            if (typeof window.Livewire.emit === 'function') {
                window.Livewire.emit('loan-order-ga-context', payload);
            } else if (typeof window.Livewire.dispatch === 'function') {
                window.Livewire.dispatch('loan-order-ga-context', {context: payload});
            }
        },
    };
}
